import { html, PropertyValues } from "lit"
import { customElement, property, state } from "lit/decorators.js"
import { DateTime, Duration } from "luxon"
import { PortalElementBase } from "./_PortalElementBase"

@customElement("session-timer")
class SessionTimer extends PortalElementBase {
    @property({
        converter: {
            fromAttribute: (v) => DateTime.fromISO(v),
            toAttribute: (v: DateTime) => v.toISO(),
        },
        attribute: "session-end",
    })
    sessionEndTime: DateTime

    @state()
    private remaining: Duration

    keepAliveOnRemainMills: number | null = null

    private updateTimer: NodeJS.Timeout
    sessionDuration: Duration | undefined

    private isExpired = false

    protected override willUpdate(
        _changedProperties: PropertyValues<this>
    ): void {
        if (_changedProperties.has("sessionEndTime")) {
            this.sessionDuration = this.sessionEndTime.diffNow()
            this.updateRemaining()
        }
    }

    override connectedCallback(): void {
        super.connectedCallback()
        this.updateTimer = setInterval(this.updateRemaining.bind(this), 1000)
    }

    override disconnectedCallback(): void {
        clearInterval(this.updateTimer)
    }

    protected render(): unknown {
        const percentage = Math.floor(
            (this.remaining.toMillis() / this.sessionDuration.toMillis()) * 100
        )

        return html`<div class="progress" style="height: 14px">
            <div
                aria-label="Session-Countdown"
                aria-valuemax="100"
                aria-valuemin="0"
                aria-valuenow="${percentage}"
                class="progress-bar"
                id="countdown"
                role="progressbar"
                style="width: ${percentage}%;"
            >
                ${this.remaining.toFormat("m:ss")}
            </div>
        </div>`
    }

    updateRemaining() {
        if (this.sessionEndTime == null) return
        this.remaining = this.sessionEndTime.diffNow()

        const remainingMills = this.remaining.toMillis()

        if (
            this.keepAliveOnRemainMills != null &&
            remainingMills <= this.keepAliveOnRemainMills
        )
            this.revalidateSession()

        if (remainingMills <= 0) this._onExpiring()
    }

    /**
     * Sends a Session KeepAlive request to the server and refreshes the end session countdown
     * @param revalidateDuration Session Duration
     */
    private async revalidateSession() {
        const endTime = DateTime.now().plus(this.sessionDuration)

        const result = await fetch("/Account/KeepAlive")
        if (result.ok) {
            this.sessionEndTime = endTime
        }
    }

    private _onExpiring() {
        if (this.isExpired) return

        location.href = "/"
        this.isExpired = true
    }

    enableKeepAlive() {
        this.keepAliveOnRemainMills = this.sessionDuration.toMillis() >> 1
    }
}

;(globalThis as any).scheduleRevalidateSession = () => {
    ;(document.querySelector("session-timer") as SessionTimer).enableKeepAlive()
}
